import { useEffect, useState } from 'react';
import api, { ApiError, LETTERHEAD_URL } from '../../api/client.js';
import { printSchedule } from '../../print/printSchedule.js';
import PrintDocument from '../../print/PrintDocument.jsx';
import { todayStr } from '../../lib/dates.js';

/**
 * Sample rows for the trial print. The purpose of that print is to check the
 * margins against the uploaded letterhead, so representative text of realistic
 * length is what matters, not real appointments.
 */
const SAMPLE_MEETINGS = [
  {
    id: 'sample-1',
    date: todayStr(),
    time: '08:30',
    title: 'اجتماع مع مديري المؤسسات العامة',
    type: 'رسمي',
    location: 'قاعة الاجتماعات الكبرى',
    notes: 'نموذج تجريبي لضبط الهوامش',
    status: 'مكتمل',
  },
  {
    id: 'sample-2',
    date: todayStr(),
    time: '11:15',
    title: 'استقبال وفد من أهالي المحافظة',
    type: 'مواطنين',
    location: 'مكتب المحافظ',
    notes: '',
    status: 'قادم',
  },
  {
    id: 'sample-3',
    date: todayStr(),
    time: '15:30',
    title: 'زيارة ميدانية لمشروع الصرف الصحي',
    type: 'ميدانية',
    location: 'الحي الشرقي',
    notes: 'برفقة مدير الخدمات الفنية',
    status: 'قادم',
  },
];

const EDITABLE_TEXT_KEYS = [
  'app_title',
  'org_name',
  'dept_name',
  'signature_label',
  'print_state_name',
  'print_footer_text',
];
const PRINT_KEYS = [
  'print_rows_per_page',
  'print_pad_top_mm',
  'print_pad_side_mm',
  'print_pad_bottom_mm',
];

export default function SettingsPanel({
  settings,
  hasLetterhead,
  letterheadSource,
  onSettingsChanged,
  showToast,
}) {
  const [form, setForm] = useState({});
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  // Bumped after an upload so the <img> refetches instead of showing the cached
  // previous artwork.
  const [previewKey, setPreviewKey] = useState(0);

  useEffect(() => {
    if (!settings) return;
    const next = {};
    for (const key of [...EDITABLE_TEXT_KEYS, ...PRINT_KEYS]) {
      next[key] = settings[key] ?? '';
    }
    setForm(next);
  }, [settings]);

  async function handleSave(event) {
    event.preventDefault();
    if (saving) return;

    setSaving(true);
    setErrors({});
    try {
      const result = await api.updateSettings(form);
      onSettingsChanged(result.settings, result.hasLetterhead, result.letterheadSource);
      showToast('تم حفظ الإعدادات');
    } catch (caught) {
      if (caught instanceof ApiError && caught.details) setErrors(caught.details);
      showToast(caught?.message || 'تعذّر حفظ الإعدادات', 'error');
    } finally {
      setSaving(false);
    }
  }

  async function handleUpload(event) {
    const file = event.target.files?.[0];
    // Reset immediately so re-selecting the same file fires change again.
    event.target.value = '';
    if (!file) return;

    setUploading(true);
    try {
      const result = await api.uploadLetterhead(file);
      onSettingsChanged(result.settings, result.hasLetterhead, result.letterheadSource);
      setPreviewKey((key) => key + 1);
      showToast('تم رفع الترويسة');
    } catch (caught) {
      showToast(caught?.message || 'تعذّر رفع الترويسة', 'error');
    } finally {
      setUploading(false);
    }
  }

  async function handleRemove() {
    try {
      const result = await api.deleteLetterhead();
      onSettingsChanged(result.settings, result.hasLetterhead, result.letterheadSource);
      setPreviewKey((key) => key + 1);
      showToast(
        result.letterheadSource === 'file'
          ? 'تم حذف الصورة المرفوعة، وعاد النظام إلى ملف letterhead.html'
          : 'تم حذف الترويسة'
      );
    } catch (caught) {
      showToast(caught?.message || 'تعذّر حذف الترويسة', 'error');
    }
  }

  function field(key, label, hint, inputProps = {}) {
    return (
      <div className="field" key={key}>
        <label htmlFor={`setting-${key}`}>{label}</label>
        <input
          id={`setting-${key}`}
          value={form[key] ?? ''}
          onChange={(event) => setForm({ ...form, [key]: event.target.value })}
          aria-invalid={Boolean(errors[key])}
          {...inputProps}
        />
        {errors[key] && <div className="field-error">{errors[key]}</div>}
        {hint && <p className="hint">{hint}</p>}
      </div>
    );
  }

  if (!settings) return <div className="loading-state">...جاري التحميل</div>;

  return (
    <div className="stack">
      <section className="panel">
        <div className="panel-header">
          <div>
            <h2>بيانات الجهة</h2>
            <p>تظهر في ترويسة الشاشة وفي الجدول المطبوع.</p>
          </div>
        </div>
        <div className="panel-body">
          <form onSubmit={handleSave}>
            {field('app_title', 'عنوان التطبيق')}
            {field('org_name', 'اسم الجهة')}
            {field('dept_name', 'اسم المديرية أو الدائرة')}
            {field(
              'signature_label',
              'نص جهة التوقيع',
              'يظهر في أسفل الصفحة الأخيرة من الجدول المطبوع.'
            )}

            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? '...جاري الحفظ' : 'حفظ'}
            </button>
          </form>
        </div>
      </section>

      <section className="panel">
        <div className="panel-header">
          <div>
            <h2>رأس وتذييل الصفحة المطبوعة</h2>
            <p>
              يُستخدمان عند عدم رفع صورة ترويسة، ويتكرّران في كل صفحة. عند رفع صورة
              الترويسة الرسمية يعتمد النظام على رأس وتذييل الصورة بدلاً منهما.
            </p>
          </div>
        </div>
        <div className="panel-body">
          <form onSubmit={handleSave}>
            {field(
              'print_state_name',
              'السطر العلوي',
              'يظهر فوق اسم الجهة، مثل: الجمهورية العربية السورية. اتركه فارغاً لإخفائه.'
            )}
            {field(
              'print_footer_text',
              'نص التذييل',
              'يظهر أسفل كل صفحة، مثل العنوان أو رقم الهاتف.'
            )}

            <div className="row">
              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving ? '...جاري الحفظ' : 'حفظ'}
              </button>
              <button type="button" className="btn" onClick={() => printSchedule()}>
                🖨 طباعة صفحة تجريبية
              </button>
            </div>
          </form>
        </div>
      </section>

      <section className="panel">
        <div className="panel-header">
          <div>
            <h2>ترويسة الطباعة الرسمية</h2>
            <p>
              صورة A4 كاملة بمساحة فارغة في الوسط. المقاس المفضّل 2481 × 3508 بكسل
              (‎300 نقطة/إنش)، بصيغة PNG أو JPEG.
            </p>
          </div>
        </div>
        <div className="panel-body">
          <div className="row" style={{ alignItems: 'flex-start', gap: '24px' }}>
            <div className="stack" style={{ flex: 1, minWidth: '240px' }}>
              {letterheadSource === 'file' && (
                <div className="form-success" style={{ marginBottom: 0 }}>
                  الترويسة الرسمية مثبّتة من ملف <code>letterhead.html</code> في مجلد
                  التطبيق، وتظهر في كل صفحة مطبوعة. لا حاجة لرفع أي صورة.
                </div>
              )}

              <div>
                <label className="btn btn-gold" style={{ display: 'inline-flex' }}>
                  {uploading
                    ? '...جاري الرفع'
                    : letterheadSource === 'upload'
                      ? 'استبدال الصورة'
                      : 'رفع صورة بديلة'}
                  <input
                    type="file"
                    accept="image/png,image/jpeg"
                    onChange={handleUpload}
                    disabled={uploading}
                    className="visually-hidden"
                  />
                </label>

                {letterheadSource === 'upload' && (
                  <button
                    type="button"
                    className="btn btn-danger"
                    onClick={handleRemove}
                    style={{ marginInlineStart: '8px' }}
                  >
                    حذف الصورة المرفوعة
                  </button>
                )}
              </div>

              {letterheadSource === 'file' && (
                <p className="hint">
                  رفع صورة هنا يتجاوز الملف المثبّت مؤقتاً. حذف الصورة المرفوعة يعيد
                  النظام إلى ملف <code>letterhead.html</code>.
                </p>
              )}

              {letterheadSource === 'upload' && (
                <p className="hint">
                  الترويسة الحالية صورة مرفوعة من هذه الصفحة.
                </p>
              )}

              {letterheadSource === 'none' && (
                <p className="hint">
                  لا توجد ترويسة. يمكن رفع صورة هنا، أو وضع ملف{' '}
                  <code>letterhead.html</code> يحتوي على بيانات الصورة بصيغة Base64 في
                  مجلد التطبيق. تتم الطباعة حالياً برأس وتذييل نصّيين، والنظام يعمل بشكل
                  كامل.
                </p>
              )}
            </div>

            {hasLetterhead && (
              <img
                key={previewKey}
                className="letterhead-preview"
                src={`${LETTERHEAD_URL}?v=${previewKey}`}
                alt="معاينة الترويسة"
              />
            )}
          </div>
        </div>
      </section>

      <section className="panel">
        <div className="panel-header">
          <div>
            <h2>ضبط الصفحة المطبوعة</h2>
            <p>
              الهوامش تحدّد مكان الجدول داخل الفراغ الأبيض في الترويسة. اطبع صفحة تجريبية
              بعد كل تعديل: زِد الهامش العلوي إذا تداخل الجدول مع رأس الصورة، وزِد السفلي
              إذا اصطدم بالذيل أو بمساحة التوقيع.
            </p>
          </div>
        </div>
        <div className="panel-body">
          <form onSubmit={handleSave}>
            <div className="field-row">
              {field('print_rows_per_page', 'عدد المواعيد في الصفحة', 'بين 1 و 20.', {
                inputMode: 'numeric',
                dir: 'ltr',
              })}
              {field('print_pad_top_mm', 'الهامش العلوي (مم)', null, {
                inputMode: 'decimal',
                dir: 'ltr',
              })}
            </div>
            <div className="field-row">
              {field('print_pad_side_mm', 'الهامش الجانبي (مم)', null, {
                inputMode: 'decimal',
                dir: 'ltr',
              })}
              {field('print_pad_bottom_mm', 'الهامش السفلي (مم)', null, {
                inputMode: 'decimal',
                dir: 'ltr',
              })}
            </div>

            <div className="row">
              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving ? '...جاري الحفظ' : 'حفظ'}
              </button>
              <button type="button" className="btn" onClick={() => printSchedule()}>
                🖨 طباعة صفحة تجريبية
              </button>
            </div>

            <p className="hint" style={{ marginTop: '12px' }}>
              إعدادات الطباعة في المتصفح: الحجم A4، الاتجاه عمودي، المقياس 100٪، الهوامش
              «بدون»، وإلغاء رؤوس وتذييلات المتصفح.
            </p>
          </form>
        </div>
      </section>

      {/*
        The trial print needs its own print document. The one on the daily
        schedule is not mounted here, and printing with none present produces a
        blank sheet — the interface itself is hidden by the print stylesheet.
      */}
      <PrintDocument
        meetings={SAMPLE_MEETINGS}
        selectedDate={todayStr()}
        settings={settings}
        hasLetterhead={hasLetterhead}
      />
    </div>
  );
}
